import { useRef, useState } from "react";
import { Play } from "lucide-react";

/**
 * One gallery tile.
 *
 * Click-to-play rather than autoplay: six autoplaying videos on a marketing
 * page is a large amount of bandwidth spent before the visitor has asked for
 * anything, and moving video that starts on its own is a WCAG 2.2.2 problem.
 * The poster frame carries the page until then.
 */
export default function GalleryCard({ demo }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  // A missing file must fall back to the placeholder tile, not leave a black
  // player reading 0:00 -- which is what the page showed when the sample
  // files were taken out but still listed.
  const [broken, setBroken] = useState(false);

  const play = () => {
    const el = videoRef.current;
    if (!el) return;
    setPlaying(true);
    // Older Safari returns undefined rather than a promise here.
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => setPlaying(false));
  };

  return (
    <div className="brutal-card p-0 overflow-hidden" data-testid={`gallery-card-${demo.id}`}>
      <div className="aspect-video bg-surface-inverse relative">
        {demo.video && !broken ? (
          <>
            <video
              ref={videoRef}
              className="w-full h-full object-contain"
              poster={demo.poster || undefined}
              preload="none"
              playsInline
              controls={playing}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              data-testid={`gallery-video-${demo.id}`}
            >
              <source src={demo.video} type="video/mp4" onError={() => setBroken(true)} />
              {demo.captions && (
                <track kind="captions" src={demo.captions} srcLang="en" label="English" default />
              )}
              Your browser cannot play this video.
            </video>
            {!playing && (
              <button
                type="button"
                onClick={play}
                aria-label={`Play ${demo.title}`}
                className="absolute inset-0 group"
                data-testid={`gallery-play-${demo.id}`}
              >
                {/* Solid scrim, not a gradient — this system has no gradients.
                    The poster frames are real product UI, so the title needs a
                    consistent surface behind it rather than luck. */}
                <span className="absolute inset-0 bg-surface-inverse/70 group-hover:bg-surface-inverse/55 transition-colors" aria-hidden="true" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="brutal-border bg-brand text-ink-on-accent w-14 h-14 flex items-center justify-center shadow-brutal">
                    <Play size={22} aria-hidden="true" />
                  </span>
                </span>
                {/* Title sits in its own band at the foot of the tile so it can
                    never collide with the play affordance at any tile width. */}
                <span className="absolute inset-x-0 bottom-0 px-3 py-2 text-left font-display font-bold text-ink-inverse type-subheading leading-tight">
                  {demo.title}
                </span>
              </button>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="absolute inset-0 bg-surface-inverse" aria-hidden="true" />
            <h2 className="relative font-display font-bold text-ink-inverse type-subheading px-4 text-center">
              {demo.title}
            </h2>
            <div className="absolute top-3 right-3 marquee-tag text-2xs">SAMPLE</div>
          </div>
        )}
      </div>
      <div className="p-4 text-xs uppercase tracking-widest flex justify-between gap-2">
        <span>{demo.format}</span>
        <span className="text-ink-muted">{demo.tone}</span>
      </div>
    </div>
  );
}
